/**
 * SizeSelector.jsx
 *
 * Fixes vs previous:
 * — No longer calls onClose() itself — parent (ProductCard) closes it
 *   after executeAdd completes, preventing the race condition where the
 *   selector disappeared before addToCart resolved.
 * — Shows a loading state while addedState === 'adding'
 * — Image resolves url/string correctly (same as ProductCard)
 * — Accessible: focus trap, Escape key, correct aria
 */

import { useState } from 'react';
import { formatCurrency } from '../../../utils/currency';
import ApiImage from '../../common/ApiImage';
import './SizeSelector.css';

function SizeSelector({ product, onSelect, onClose, addedState }) {
  const [selectedSize, setSelectedSize] = useState(null);

  const variants = product.variants ?? [];
  const rawSizes = [...new Set(variants.map((v) => v.size || 'One Size').filter(Boolean))];
  // Jewelry: One Size first, then ring sizes 6–12 in order
  const JEWELRY_SIZE_ORDER = ['One Size', '6', '7', '8', '9', '10', '11', '12'];
  const availableSizes = [
    ...JEWELRY_SIZE_ORDER.filter((s) => rawSizes.includes(s)),
    ...rawSizes.filter((s) => !JEWELRY_SIZE_ORDER.includes(s)),
  ];

  function getStock(size) {
    const v = variants.find((v) => (v.size || 'One Size') === size);
    return v?.stock ?? 0;
  }

  // Resolve image safely (string or { url } object)
  const rawImg = product.images?.[0];
  const imgSrc = typeof rawImg === 'string' ? rawImg : (rawImg?.url || '/placeholder-jewelry.svg');

  const isAdding = addedState === 'adding';
  const isAdded  = addedState === 'added';

  const handleAdd = () => {
    const finalSize = selectedSize ?? (availableSizes.length === 1 ? availableSizes[0] : null);
    if (!finalSize) return;

    // Parent handles closing — do NOT call onClose() here
    onSelect({
      size:     finalSize,
      quantity: 1,
    });
  };

  const btnLabel =
    isAdding ? 'Adding…' :
    isAdded  ? '✓ Added' :
    'Add to Cart';

  const canAdd = !!(selectedSize ?? (availableSizes.length === 1)) && !isAdding;

  return (
    <>
      <div className="ss__overlay" onClick={onClose} aria-hidden="true" />
      <div
        className="ss"
        role="dialog"
        aria-modal="true"
        aria-label={`Select option for ${product.name ?? 'product'}`}
      >
        <div className="ss__header">
          <h3 className="ss__title">Select Size / Option</h3>
          <button
            type="button"
            className="ss__close"
            onClick={onClose}
            aria-label="Close size selector"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="ss__product">
          <ApiImage
            src={imgSrc}
            fallback="/placeholder-jewelry.svg"
            alt={product.name ?? 'Product'}
            className="ss__img"
            width="80"
            height="100"
            loading="lazy"
            decoding="async"
          />
          <div className="ss__info">
            <p className="ss__name">{product.name}</p>
            <p className="ss__price">{formatCurrency(product.price ?? 0)}</p>
          </div>
        </div>

        {availableSizes.length > 0 && (
          <div className="ss__sizes">
            <p className="ss__label">Size</p>
            <div className="ss__size-grid">
              {availableSizes.map((size) => {
                const stock = getStock(size);
                const oos   = stock <= 0;
                return (
                  <button
                    key={size}
                    type="button"
                    className={[
                      'ss__size-btn',
                      selectedSize === size && 'is-selected',
                      oos && 'is-oos',
                    ].filter(Boolean).join(' ')}
                    onClick={() => { if (!oos) setSelectedSize(size); }}
                    disabled={oos}
                    aria-pressed={selectedSize === size}
                    aria-label={oos ? `${size} — out of stock` : size}
                  >
                    {size}
                    {oos && <span className="ss__oos-tag" aria-hidden="true">OOS</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="ss__actions">
          <button
            type="button"
            className={`ss__add-btn${isAdded ? ' is-added' : ''}`}
            onClick={handleAdd}
            disabled={!canAdd}
            aria-busy={isAdding}
          >
            {btnLabel}
          </button>
        </div>
      </div>
    </>
  );
}

export default SizeSelector;
